import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ArrowRightIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
interface DeckShellProps {
  slides: React.ReactNode[];
  theme?: 'dark' | 'light';
  brandLabel?: string;
}
export function DeckShell({
  slides,
  theme = 'dark',
  brandLabel = 'Offsite+'
}: DeckShellProps) {
  const [current, setCurrent] = useState(0);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const navigate = useNavigate();
  const next = useCallback(() => setCurrent(p => Math.min(p + 1, slides.length - 1)), [slides.length]);
  const prev = useCallback(() => setCurrent(p => Math.max(p - 1, 0)), []);
  // Auto-hide the mobile swipe hint after a few seconds, and hide as soon
  // as the user advances to slide 2+.
  useEffect(() => {
    const t = setTimeout(() => setShowSwipeHint(false), 4000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (current > 0) setShowSwipeHint(false);
  }, [current]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        next();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
      if (e.key === 'Home') setCurrent(0);
      if (e.key === 'End') setCurrent(slides.length - 1);
      if (e.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, prev, navigate, slides.length]);
  // Touch support — only trigger slide nav on a clearly horizontal swipe,
  // so vertical scroll inside a slide keeps working on mobile.
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => setTouchStart({
    x: e.targetTouches[0].clientX,
    y: e.targetTouches[0].clientY
  });
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const dx = touchStart.x - endX;
    const dy = touchStart.y - endY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    // Require a meaningful horizontal swipe (>60px) and clearly more
    // horizontal than vertical (avoids hijacking vertical scrolling).
    if (absDx > 60 && absDx > absDy * 1.4) {
      if (dx > 0) next();else prev();
    }
    setTouchStart(null);
  };
  const isDark = theme === 'dark';
  const bgClass = isDark ? 'bg-[#0A0F1F] text-white' : 'bg-[#FAF6EF] text-slate-900';
  const controlBg = isDark ? 'bg-[#0A0F1F]/80 border-white/10' : 'bg-[#FAF6EF]/80 border-slate-200';
  const textClass = isDark ? 'text-white' : 'text-slate-900';
  const mutedText = isDark ? 'text-slate-400' : 'text-slate-500';
  const activeDot = isDark ? 'bg-brand-coral' : 'bg-amber-600';
  const inactiveDot = isDark ? 'bg-white/20' : 'bg-slate-300';
  return <div className={`fixed inset-0 w-screen h-screen overflow-hidden ${bgClass} font-sans`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-1877">
      <Link to="/" className={`absolute top-6 right-6 z-50 p-2 rounded-full ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-900'} transition-colors`} data-id="element-1878">
        <XIcon className="w-5 h-5" data-id="element-1879" />
      </Link>

      <div className="relative w-full h-[calc(100dvh-4rem)] md:h-[calc(100vh-4rem)] overflow-hidden" style={{
      touchAction: 'pan-y'
    }} data-id="element-1880">
        <AnimatePresence mode="wait" data-id="element-1881">
          <motion.div key={current} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute inset-0 w-full h-full overflow-y-auto overflow-x-hidden overscroll-contain" data-id="element-1882">
            <div className="min-h-full w-full flex flex-col" data-id="element-1883">
              {slides[current]}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Mobile floating nav arrows — subtle, get out of the way of content */}
        {current > 0 && <button onClick={prev} aria-label="Previous slide" className={`md:hidden absolute left-1.5 top-1/2 -translate-y-1/2 z-40 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center active:scale-90 transition-all ${isDark ? 'bg-white/[0.08] text-white/60 border border-white/10 active:bg-white/15' : 'bg-black/5 text-slate-700 border border-slate-900/10 active:bg-black/10'}`} data-id="element-1884">
            <ChevronLeftIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-1885" />
          </button>}
        {current < slides.length - 1 && <button onClick={next} aria-label="Next slide" className={`md:hidden absolute right-1.5 top-1/2 -translate-y-1/2 z-40 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center active:scale-90 transition-all ${isDark ? 'bg-white/[0.08] text-white/60 border border-white/10 active:bg-white/15' : 'bg-black/5 text-slate-700 border border-slate-900/10 active:bg-black/10'}`} data-id="element-1886">
            <ChevronRightIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-1887" />
          </button>}

        {/* First-load swipe hint (mobile only) */}
        <AnimatePresence data-id="element-1888">
          {showSwipeHint && current === 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4
        }} className="md:hidden absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-none" data-id="element-1889">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md text-xs font-semibold shadow-lg ${isDark ? 'bg-white/15 text-white border border-white/20' : 'bg-white/90 text-slate-900 border border-slate-200'}`} data-id="element-1890">
                <motion.span animate={{
              x: [0, 8, 0]
            }} transition={{
              duration: 1.4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="flex items-center gap-2" data-id="element-1891">
                  Swipe or tap
                  <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1892" />
                </motion.span>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className={`absolute bottom-0 left-0 right-0 h-16 ${controlBg} backdrop-blur-md border-t flex items-center justify-between px-6 z-50`} data-id="element-1893">
        <div className="flex items-center gap-3" data-id="element-1894">
          <div className="flex items-center gap-2" data-id="element-1895">
            <svg viewBox="0 0 32 32" className="w-5 h-5 shrink-0" fill="none" data-id="element-1896">
              <circle cx="16" cy="16" r="13" stroke={isDark ? '#fff' : '#0B1B3B'} strokeWidth="4" strokeLinecap="round" strokeDasharray="55 25" transform="rotate(-90 16 16)" data-id="element-1897" />
              <circle cx="16" cy="16" r="13" stroke="#0EA5A4" strokeWidth="4" strokeLinecap="round" strokeDasharray="20 80" transform="rotate(-90 16 16)" data-id="element-1898" />
              <circle cx="16" cy="3" r="2" fill="#FF6B4A" data-id="element-1899" />
            </svg>
            <span className={`font-bold text-sm ${textClass} hidden sm:block`} data-id="element-1900">
              {brandLabel}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-1.5 absolute left-1/2 -translate-x-1/2" data-id="element-1901">
          <div className={`text-[10px] font-bold tracking-[0.2em] ${mutedText}`} data-id="element-1902">
            {String(current + 1).padStart(2, '0')} /{' '}
            {String(slides.length).padStart(2, '0')}
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1903">
            {slides.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === current ? activeDot : inactiveDot}`} aria-label={`Go to slide ${i + 1}`} data-id="element-1904" />)}
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-1905">
          <button onClick={prev} disabled={current === 0} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 disabled:opacity-30 text-white' : 'hover:bg-slate-200 disabled:opacity-30 text-slate-900'} transition-colors`} data-id="element-1906">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-1907" />
          </button>
          <button onClick={next} disabled={current === slides.length - 1} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 disabled:opacity-30 text-white' : 'hover:bg-slate-200 disabled:opacity-30 text-slate-900'} transition-colors`} data-id="element-1908">
            <ChevronRightIcon className="w-5 h-5" data-id="element-1909" />
          </button>
        </div>
      </div>
    </div>;
}